// frontend/src/dashboard/PaymentInstructionsEditor.tsx
//
// Editor for tenant_settings.payment_instructions — the "How to pay" block
// shown on invoice PDFs / invoice portal for unpaid invoices. Mirrors the
// backend shape + cleaning rules in api/core/tenants/payment-instructions.ts
// so a round-trip (save → reload → save) is stable:
//   - empty-string fields are dropped
//   - a method whose fields are all empty is dropped entirely
//   - qr specifically requires a url — a caption with no url doesn't survive
//     (the backend rejects that combination outright)
//   - the whole object collapses to `null` when nothing is left
//
// Controlled component: local editable state seeds from `value` and re-syncs
// whenever the incoming prop changes to something the local cleaned state
// doesn't already match (covers both initial mount and an external reset,
// e.g. SettingsPage clearing its draft after a successful save).
//
// Validation here is DISPLAY ONLY — it never blocks typing or strips a
// still-being-typed value. The backend is the source of truth for what's
// actually rejected at save time.

import * as React from 'react';
import { FieldLabel, Textarea as PdsTextarea } from '../design-system/pds';
import { ImageUploadInput } from '../design-system/components/ImageUploadInput';

export interface PaymentInstructionsValue {
  ach?: {
    bank_name?: string;
    account_name?: string;
    routing_number?: string;
    account_number?: string;
  };
  paypal?: { handle?: string };
  zelle?: { recipient_name?: string; contact?: string };
  qr?: { url: string; caption?: string };
  other?: string;
}

interface DraftAch {
  bank_name: string;
  account_name: string;
  routing_number: string;
  account_number: string;
}
interface DraftPaypal {
  handle: string;
}
interface DraftZelle {
  recipient_name: string;
  contact: string;
}
interface DraftQr {
  url: string;
  caption: string;
}
interface Draft {
  ach: DraftAch;
  paypal: DraftPaypal;
  zelle: DraftZelle;
  qr: DraftQr;
  other: string;
}

function toDraft(value: PaymentInstructionsValue | null | undefined): Draft {
  return {
    ach: {
      bank_name: value?.ach?.bank_name ?? '',
      account_name: value?.ach?.account_name ?? '',
      routing_number: value?.ach?.routing_number ?? '',
      account_number: value?.ach?.account_number ?? '',
    },
    paypal: { handle: value?.paypal?.handle ?? '' },
    zelle: {
      recipient_name: value?.zelle?.recipient_name ?? '',
      contact: value?.zelle?.contact ?? '',
    },
    qr: { url: value?.qr?.url ?? '', caption: value?.qr?.caption ?? '' },
    other: value?.other ?? '',
  };
}

/** Mirrors api/core/tenants/payment-instructions.ts's normalizer, minus the
 *  parts that throw (length caps, routing-number shape, qr host allowlist) —
 *  those stay server-enforced; this only decides what survives vs. what's
 *  empty. */
function clean(draft: Draft): PaymentInstructionsValue | null {
  const out: PaymentInstructionsValue = {};

  const ach: Record<string, string> = {};
  for (const k of ['bank_name', 'account_name', 'routing_number', 'account_number'] as const) {
    const v = draft.ach[k].trim();
    if (v) ach[k] = v;
  }
  if (Object.keys(ach).length > 0) out.ach = ach as PaymentInstructionsValue['ach'];

  const paypalHandle = draft.paypal.handle.trim();
  if (paypalHandle) out.paypal = { handle: paypalHandle };

  const zelle: Record<string, string> = {};
  const recipient = draft.zelle.recipient_name.trim();
  const contact = draft.zelle.contact.trim();
  if (recipient) zelle.recipient_name = recipient;
  if (contact) zelle.contact = contact;
  if (Object.keys(zelle).length > 0) out.zelle = zelle as PaymentInstructionsValue['zelle'];

  const qrUrl = draft.qr.url.trim();
  const qrCaption = draft.qr.caption.trim();
  // The backend requires a url whenever qr is present — a caption alone
  // can't be saved, so don't emit a qr object without one.
  if (qrUrl) out.qr = qrCaption ? { url: qrUrl, caption: qrCaption } : { url: qrUrl };

  const other = draft.other.trim();
  if (other) out.other = other;

  return Object.keys(out).length === 0 ? null : out;
}

function deepEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

const inputClass =
  'h-11 w-full rounded-2xl px-3.5 text-[13.5px] text-ink dark:text-white bg-white/70 dark:bg-white/[0.05] ring-1 ring-ink/10 dark:ring-white/10 focus:outline-none focus:ring-2 focus:ring-coral/40 transition-shadow placeholder:text-ink/35 dark:placeholder:text-white/35';

function MethodCard({
  title,
  children,
}: {
  title: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl bg-white/50 dark:bg-white/[0.03] ring-1 ring-ink/[0.06] dark:ring-white/[0.08] p-4 sm:p-5">
      <h4 className="text-[13px] font-semibold text-ink dark:text-white mb-3">{title}</h4>
      <div className="grid gap-3 sm:grid-cols-2">{children}</div>
    </div>
  );
}

export default function PaymentInstructionsEditor({
  value,
  onChange,
}: {
  value: PaymentInstructionsValue | null;
  onChange: (v: PaymentInstructionsValue | null) => void;
}) {
  const [draft, setDraft] = React.useState<Draft>(() => toDraft(value));

  // Re-sync from an external reset (e.g. SettingsPage clearing its draft
  // after save) — but only when the incoming value doesn't already match
  // what we'd emit for the current draft, so we don't clobber in-progress
  // typing on every render.
  const lastEmitted = React.useRef<PaymentInstructionsValue | null>(clean(toDraft(value)));
  React.useEffect(() => {
    if (!deepEqual(value, lastEmitted.current)) {
      setDraft(toDraft(value));
      lastEmitted.current = clean(toDraft(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  function update(next: Draft) {
    setDraft(next);
    const cleaned = clean(next);
    lastEmitted.current = cleaned;
    onChange(cleaned);
  }

  function setAch<K extends keyof DraftAch>(k: K, v: string) {
    update({ ...draft, ach: { ...draft.ach, [k]: v } });
  }
  function setPaypal(v: string) {
    update({ ...draft, paypal: { handle: v } });
  }
  function setZelle<K extends keyof DraftZelle>(k: K, v: string) {
    update({ ...draft, zelle: { ...draft.zelle, [k]: v } });
  }
  function setQr<K extends keyof DraftQr>(k: K, v: string) {
    update({ ...draft, qr: { ...draft.qr, [k]: v } });
  }
  function setOther(v: string) {
    update({ ...draft, other: v });
  }

  const routingInvalid =
    draft.ach.routing_number.trim().length > 0 && !/^\d{9}$/.test(draft.ach.routing_number.trim());

  return (
    <div className="grid gap-4">
      <MethodCard title="Bank transfer (ACH)">
        <div>
          <FieldLabel htmlFor="pi-ach-bank-name">Bank name</FieldLabel>
          <input
            id="pi-ach-bank-name"
            className={inputClass}
            value={draft.ach.bank_name}
            onChange={(e) => setAch('bank_name', e.target.value)}
            placeholder="First National Bank"
          />
        </div>
        <div>
          <FieldLabel htmlFor="pi-ach-account-name">Account name</FieldLabel>
          <input
            id="pi-ach-account-name"
            className={inputClass}
            value={draft.ach.account_name}
            onChange={(e) => setAch('account_name', e.target.value)}
            placeholder="Acme Inc."
          />
        </div>
        <div>
          <FieldLabel htmlFor="pi-ach-routing">Routing number</FieldLabel>
          <input
            id="pi-ach-routing"
            className={inputClass}
            value={draft.ach.routing_number}
            onChange={(e) => setAch('routing_number', e.target.value)}
            placeholder="123456789"
            inputMode="numeric"
          />
          {routingInvalid && (
            <p className="text-[11.5px] text-amber-600 dark:text-amber-400 mt-1.5">
              Routing numbers are 9 digits
            </p>
          )}
        </div>
        <div>
          <FieldLabel htmlFor="pi-ach-account-number">Account number</FieldLabel>
          <input
            id="pi-ach-account-number"
            className={inputClass}
            value={draft.ach.account_number}
            onChange={(e) => setAch('account_number', e.target.value)}
            placeholder="000123456789"
          />
        </div>
      </MethodCard>

      <MethodCard title="PayPal">
        <div className="sm:col-span-2">
          {/* No visible <FieldLabel> here — the card title already reads
              "PayPal", and this field's own label text is "PayPal email or
              link"; keeping both as visible text would give getByText(/paypal/i)
              two matches. `aria-label` still satisfies getByLabelText. */}
          <input
            id="pi-paypal-handle"
            aria-label="PayPal email or link"
            className={inputClass}
            value={draft.paypal.handle}
            onChange={(e) => setPaypal(e.target.value)}
            placeholder="billing@yourcompany.com or paypal.me/yourcompany"
          />
        </div>
      </MethodCard>

      <MethodCard title="Zelle">
        <div>
          <FieldLabel htmlFor="pi-zelle-recipient">Recipient name</FieldLabel>
          <input
            id="pi-zelle-recipient"
            className={inputClass}
            value={draft.zelle.recipient_name}
            onChange={(e) => setZelle('recipient_name', e.target.value)}
            placeholder="Acme Inc."
          />
        </div>
        <div>
          <FieldLabel htmlFor="pi-zelle-contact">Email or U.S. mobile number</FieldLabel>
          <input
            id="pi-zelle-contact"
            className={inputClass}
            value={draft.zelle.contact}
            onChange={(e) => setZelle('contact', e.target.value)}
            placeholder="billing@yourcompany.com or +1 (555) 010-0000"
          />
        </div>
      </MethodCard>

      <MethodCard title="QR code">
        <div className="sm:col-span-2">
          <ImageUploadInput
            value={draft.qr.url}
            onChange={(url) => setQr('url', url)}
            endpoint="tenant"
            label="QR image"
            helperText="Upload the QR from your banking or payment app."
          />
        </div>
        <div className="sm:col-span-2">
          <FieldLabel htmlFor="pi-qr-caption">Caption</FieldLabel>
          <input
            id="pi-qr-caption"
            className={inputClass}
            value={draft.qr.caption}
            onChange={(e) => setQr('caption', e.target.value)}
            placeholder="Scan to pay via UPI"
          />
        </div>
      </MethodCard>

      <MethodCard title="Other instructions">
        <div className="sm:col-span-2">
          <FieldLabel htmlFor="pi-other">Details</FieldLabel>
          <PdsTextarea
            id="pi-other"
            rows={3}
            value={draft.other}
            onChange={(e) => setOther(e.target.value)}
            placeholder="Anything else — e.g. check mailing address or Wise details."
          />
          <p className="text-[11.5px] text-ink/50 dark:text-white/45 mt-1.5">
            Anything else — e.g. check mailing address or Wise details.
          </p>
        </div>
      </MethodCard>
    </div>
  );
}
